/**
 * Transport over the yAzzI relay Worker (see workers/relay). One WebSocket
 * per device; the host multiplexes every guest over it as a virtual Conn.
 *
 * Stale-socket safety: every handler checks that the socket it fired for is
 * still the current one. Events from a superseded socket (late close, late
 * frame) are ignored, and close code 4005 "replaced" is never a kick.
 */
import { shortId } from '@/engine/rng';
import {
  TransportError,
  type Conn,
  type LinkStatus,
  type Transport,
  type TransportEvent,
} from './transport';

const DEFAULT_RELAY_URL = 'https://yazzi-relay.joel-wilson-mt.workers.dev';
export const OPEN_TIMEOUT_MS = 12_000;
const PING_EVERY_MS = 20_000;
const PONG_WAIT_MS = 10_000;
/** After the tab wakes, a pong must arrive this fast or the socket is dead. */
const PROBE_WAIT_MS = 3_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 10_000;

export function relayUrl(): string {
  const configured = (
    import.meta.env?.VITE_RELAY_URL as string | undefined
  )?.trim();
  return (configured || DEFAULT_RELAY_URL).replace(/\/$/, '');
}

function socketUrl(code: string): string {
  return `${relayUrl().replace(/^http/, 'ws')}/room/${code.toUpperCase()}`;
}

type Frame =
  | { t: 'attached'; role: 'host' | 'guest'; epoch: number; token?: string }
  | { t: 'error'; reason: string }
  | { t: 'data'; data: unknown }
  | { t: 'host'; present: boolean }
  | { t: 'peers'; clientIds: string[] }
  | { t: 'peer'; clientId: string; event: 'open' | 'close' }
  | { t: 'from'; clientId: string; data: unknown }
  | { t: 'ended' }
  | { t: 'pong' };

export function errorFor(reason: string): TransportError {
  switch (reason) {
    case 'no-room':
      return new TransportError(
        'peer-unavailable',
        'No room with that code is open.'
      );
    case 'ended':
      return new TransportError('ended', 'That room has ended.');
    case 'not-host':
      return new TransportError(
        'id-taken',
        'Someone else is hosting that room code.'
      );
    case 'expired':
      return new TransportError('peer-unavailable', 'That room has expired.');
    default:
      return new TransportError('unknown', 'The relay refused the connection.');
  }
}

interface LinkOptions {
  code: string;
  role: 'host' | 'guest';
  clientId: string;
  token?: string | null;
  /** Reconnect with backoff when the socket drops (host). */
  autoReconnect: boolean;
  onFrame: (frame: Frame) => void;
  onStatus: (status: LinkStatus) => void;
  onEvent: (event: TransportEvent) => void;
  /** The socket dropped and will not come back by itself (guest), or a terminal error. */
  onDown: (reason: TransportError | null) => void;
}

/** One WebSocket to the relay with the attach handshake and keepalive. */
class Link {
  private ws: WebSocket | null = null;
  private destroyed = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private pingSentAt = 0;
  private backoff = BACKOFF_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private opening = false;
  token: string | null;
  epoch = 0;
  private readonly opts: LinkOptions;

  constructor(opts: LinkOptions) {
    this.opts = opts;
    this.token = opts.token ?? null;
  }

  /** Open and attach; resolves with the token for hosts. */
  open(): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      if (this.destroyed) {
        reject(new TransportError('unknown', 'Transport was closed.'));
        return;
      }
      // Any previous socket is superseded from this moment on.
      const previous = this.ws;
      this.stopKeepalive();
      let ws: WebSocket;
      try {
        ws = new WebSocket(socketUrl(this.opts.code));
      } catch {
        reject(new TransportError('network', 'Could not reach the relay.'));
        return;
      }
      this.ws = ws;
      this.opening = true;
      if (previous) {
        try {
          previous.close(1000, 'superseded');
        } catch {
          /* ignore */
        }
      }
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        this.opening = false;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        this.opts.onEvent({
          type: 'error',
          message: 'Timed out reaching the relay.',
        });
        reject(new TransportError('network', 'Timed out reaching the relay.'));
      }, OPEN_TIMEOUT_MS);
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        this.opening = false;
        clearTimeout(timer);
        fn();
      };

      ws.onopen = () => {
        if (ws !== this.ws) return;
        this.rawSend(ws, {
          t: 'attach',
          role: this.opts.role,
          clientId: this.opts.clientId,
          ...(this.token ? { token: this.token } : {}),
        });
      };
      ws.onmessage = ev => {
        if (ws !== this.ws) return; // stale socket
        if (typeof ev.data !== 'string') return;
        let frame: Frame;
        try {
          frame = JSON.parse(ev.data) as Frame;
        } catch {
          return;
        }
        this.opts.onEvent({ type: 'inbound' });
        if (frame.t === 'pong') {
          if (this.pongTimer) clearTimeout(this.pongTimer);
          this.pongTimer = null;
          if (this.pingSentAt)
            this.opts.onEvent({
              type: 'pong',
              rttMs: Date.now() - this.pingSentAt,
            });
          return;
        }
        if (frame.t === 'attached') {
          if (frame.token) this.token = frame.token;
          this.epoch = frame.epoch ?? this.epoch + 1;
          this.backoff = BACKOFF_MIN_MS;
          this.startKeepalive();
          this.opts.onEvent({ type: 'attached', epoch: this.epoch });
          this.opts.onStatus('connected');
          settle(() => resolve(frame.token));
          return;
        }
        if (frame.t === 'error') {
          const err = errorFor(frame.reason);
          this.opts.onEvent({ type: 'error', message: err.message });
          if (!settled) settle(() => reject(err));
          else if (
            frame.reason === 'no-room' ||
            frame.reason === 'not-host' ||
            frame.reason === 'ended'
          ) {
            this.destroyed = true;
            this.opts.onDown(err);
          }
          return;
        }
        if (frame.t === 'ended') {
          this.destroyed = true;
          this.opts.onFrame(frame);
          this.opts.onDown(errorFor('ended'));
          return;
        }
        this.opts.onFrame(frame);
      };
      ws.onerror = () => {
        /* onclose follows */
      };
      ws.onclose = ev => {
        if (ws !== this.ws) return; // a superseded socket closing: not news
        this.stopKeepalive();
        this.ws = null;
        if (!settled) {
          const err =
            ev.code === 4004
              ? errorFor(ev.reason === 'ended' ? 'ended' : 'no-room')
              : ev.code === 4003
                ? errorFor('not-host')
                : new TransportError('network', 'Could not reach the relay.');
          this.opts.onEvent({ type: 'error', message: err.message });
          settle(() => reject(err));
          return;
        }
        if (this.destroyed) return;
        if (ev.code === 4004 || ev.code === 4003) {
          // Room gone / ended, or not ours: terminal. The room layer decides
          // whether to forget anything (it probes first).
          this.destroyed = true;
          this.opts.onDown(
            ev.code === 4004
              ? errorFor(ev.reason === 'ended' ? 'ended' : 'no-room')
              : errorFor('not-host')
          );
          return;
        }
        if (ev.code === 4005) {
          // Our current socket was replaced by another attach with the same
          // identity: another tab (or a second device with the host token).
          // Do not fight over it.
          this.opts.onEvent({
            type: 'error',
            message: 'Another tab or device took over this connection.',
          });
          this.destroyed = true;
          this.opts.onDown(null);
          return;
        }
        // 4007 stale, 1006 abnormal, network hiccup: come back.
        this.opts.onEvent({ type: 'reconnect' });
        if (this.opts.autoReconnect) this.scheduleReconnect();
        else this.opts.onDown(null);
      };
    });
  }

  private scheduleReconnect(): void {
    this.opts.onStatus('reconnecting');
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.destroyed) return;
      this.open().catch(() => {
        if (!this.destroyed) this.scheduleReconnect();
      });
    }, delay);
  }

  /** Try again right now (e.g. the tab became visible). */
  nudge(): void {
    if (this.destroyed || this.opening || !this.opts.autoReconnect) return;
    if (this.ws) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.backoff = BACKOFF_MIN_MS;
    this.open().catch(() => {
      if (!this.destroyed) this.scheduleReconnect();
    });
  }

  /**
   * The tab just woke up: the browser may think the socket is open while the
   * network already dropped it. Ping and demand a fast pong; otherwise close,
   * which triggers the normal reconnect path.
   */
  probe(): void {
    if (this.destroyed) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.nudge();
      return;
    }
    if (this.pongTimer) return; // a ping is already in flight
    this.sendPing(PROBE_WAIT_MS);
  }

  private sendPing(waitMs: number): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    this.pingSentAt = Date.now();
    try {
      ws.send('{"t":"ping"}');
    } catch {
      return;
    }
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      if (ws !== this.ws) return;
      // No pong: the socket is dead even if the browser thinks otherwise.
      this.opts.onEvent({
        type: 'error',
        message: 'No answer from the relay.',
      });
      try {
        ws.close(4008, 'no pong');
      } catch {
        /* ignore */
      }
      // Some browsers never fire onclose for a dead socket; force the path.
      if (ws === this.ws) {
        this.ws = null;
        this.stopKeepalive();
        this.opts.onEvent({ type: 'reconnect' });
        if (this.opts.autoReconnect) this.scheduleReconnect();
        else this.opts.onDown(null);
      }
    }, waitMs);
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    this.pingTimer = setInterval(
      () => this.sendPing(PONG_WAIT_MS),
      PING_EVERY_MS
    );
  }

  private stopKeepalive(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pingTimer = null;
    this.pongTimer = null;
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private rawSend(ws: WebSocket, frame: unknown): boolean {
    try {
      ws.send(JSON.stringify(frame));
      this.opts.onEvent({ type: 'outbound' });
      return true;
    } catch {
      return false;
    }
  }

  send(frame: unknown): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    return this.rawSend(this.ws, frame);
  }

  close(): void {
    this.destroyed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.stopKeepalive();
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close(1000, 'bye');
    } catch {
      /* ignore */
    }
  }
}

/** A guest as seen by the host: one virtual connection over the host link. */
class VirtualConn implements Conn {
  private dataCbs: ((d: unknown) => void)[] = [];
  private closeCbs: (() => void)[] = [];
  closed = false;
  readonly peerId: string;
  private readonly link: Link;
  constructor(peerId: string, link: Link) {
    this.peerId = peerId;
    this.link = link;
  }
  send(data: unknown): void {
    if (this.closed) return;
    this.link.send({ t: 'to', clientId: this.peerId, data });
  }
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.link.send({ t: 'drop', clientId: this.peerId });
    this.closeCbs.forEach(cb => cb());
  }
  /** The relay reported the guest socket gone. */
  gone(): void {
    if (this.closed) return;
    this.closed = true;
    this.closeCbs.forEach(cb => cb());
  }
  deliver(data: unknown): void {
    if (!this.closed) this.dataCbs.forEach(cb => cb(data));
  }
  onData(cb: (d: unknown) => void): void {
    this.dataCbs.push(cb);
  }
  onClose(cb: () => void): void {
    this.closeCbs.push(cb);
  }
}

/** The host as seen by a guest. */
class HostConn implements Conn {
  readonly peerId = 'host';
  private dataCbs: ((d: unknown) => void)[] = [];
  private closeCbs: (() => void)[] = [];
  private presenceCbs: ((p: boolean) => void)[] = [];
  private present: boolean | null = null;
  closed = false;
  private readonly link: Link;
  constructor(link: Link) {
    this.link = link;
  }
  send(data: unknown): void {
    if (!this.closed) this.link.send({ t: 'send', data });
  }
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.link.close();
    this.closeCbs.forEach(cb => cb());
  }
  down(): void {
    if (this.closed) return;
    this.closed = true;
    this.closeCbs.forEach(cb => cb());
  }
  deliver(data: unknown): void {
    if (!this.closed) this.dataCbs.forEach(cb => cb(data));
  }
  setPresence(present: boolean): void {
    this.present = present;
    this.presenceCbs.forEach(cb => cb(present));
  }
  onData(cb: (d: unknown) => void): void {
    this.dataCbs.push(cb);
  }
  onClose(cb: () => void): void {
    this.closeCbs.push(cb);
  }
  onHostPresence(cb: (present: boolean) => void): void {
    this.presenceCbs.push(cb);
    if (this.present !== null) cb(this.present);
  }
}

export function createRelayTransport(): Transport {
  let link: Link | null = null;
  const statusCbs: ((s: LinkStatus) => void)[] = [];
  const eventCbs: ((e: TransportEvent) => void)[] = [];
  const conns = new Map<string, VirtualConn>();
  let onConnectionCb: ((conn: Conn) => void) | null = null;
  const clientId = `${shortId(6)}-${shortId(6)}`;
  let hostConn: HostConn | null = null;
  /** The last terminal reason (surfaced by the room layer). */
  let lastDown: TransportError | null = null;

  const emitStatus = (s: LinkStatus) => statusCbs.forEach(cb => cb(s));
  const emitEvent = (e: TransportEvent) => eventCbs.forEach(cb => cb(e));

  const onWake = () => {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState !== 'visible'
    )
      return;
    link?.probe();
  };
  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('pageshow', onWake);
    window.addEventListener('online', onWake);
  }

  /**
   * Ensure a live virtual conn for a guest clientId. A conn that was closed
   * (dropped while our link was down, or timed out by the room) is replaced
   * with a fresh one so the room can seat the returning guest again.
   */
  function openGuest(id: string): VirtualConn {
    const existing = conns.get(id);
    if (existing && !existing.closed) return existing;
    if (existing) conns.delete(id);
    const conn = new VirtualConn(id, link!);
    conns.set(id, conn);
    onConnectionCb?.(conn);
    return conn;
  }

  function hostFrame(frame: Frame): void {
    switch (frame.t) {
      case 'peers': {
        const present = new Set(frame.clientIds);
        for (const [id, conn] of conns) {
          if (!present.has(id)) {
            conns.delete(id);
            conn.gone();
          }
        }
        for (const id of frame.clientIds) openGuest(id);
        break;
      }
      case 'peer':
        if (frame.event === 'open') openGuest(frame.clientId);
        else {
          const conn = conns.get(frame.clientId);
          conns.delete(frame.clientId);
          conn?.gone();
        }
        break;
      case 'from':
        // Anything from a guest proves it is attached: make sure it has a live conn.
        openGuest(frame.clientId).deliver(frame.data);
        break;
      default:
        break;
    }
  }

  function guestFrame(frame: Frame): void {
    switch (frame.t) {
      case 'data':
        hostConn?.deliver(frame.data);
        break;
      case 'host':
        hostConn?.setPresence(frame.present);
        break;
      case 'ended':
        hostConn?.setPresence(false);
        break;
      default:
        break;
    }
  }

  return {
    async listen(code, onConnection, token) {
      onConnectionCb = onConnection;
      link?.close();
      lastDown = null;
      link = new Link({
        code,
        role: 'host',
        clientId,
        token: token ?? null,
        autoReconnect: true,
        onFrame: hostFrame,
        onStatus: emitStatus,
        onEvent: emitEvent,
        onDown: reason => {
          lastDown = reason;
          for (const conn of conns.values()) conn.gone();
          conns.clear();
          emitStatus('reconnecting');
        },
      });
      const got = await link.open();
      return { token: got ?? link.token ?? '' };
    },

    async connect(code) {
      link?.close();
      hostConn?.down();
      lastDown = null;
      const l = new Link({
        code,
        role: 'guest',
        clientId,
        autoReconnect: false,
        onFrame: guestFrame,
        onStatus: emitStatus,
        onEvent: emitEvent,
        onDown: reason => {
          lastDown = reason;
          hostConn?.down();
          emitStatus('reconnecting');
        },
      });
      link = l;
      const conn = new HostConn(l);
      hostConn = conn;
      await l.open();
      return conn;
    },

    onStatus(cb) {
      statusCbs.push(cb);
    },

    onEvent(cb) {
      eventCbs.push(cb);
    },

    probe() {
      link?.probe();
    },

    end() {
      link?.send({ t: 'end' });
    },

    destroy() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('visibilitychange', onWake);
        window.removeEventListener('pageshow', onWake);
        window.removeEventListener('online', onWake);
      }
      link?.close();
      link = null;
      for (const conn of conns.values()) conn.gone();
      conns.clear();
      hostConn = null;
      void lastDown;
    },
  };
}
